// Import CSS
import './AssessmentFormCheckGroup.scss'

// React and Third Party Libraries
import { useState } from 'react'
import { Form } from 'react-bootstrap'
import cn from 'classnames'

/**
 * Assessment Form Screen Check Group component: used to render an group of checkboxes or radio buttons
 * @param {Object} initProps - initial props
 * @param {Object} initProps.questionData - question data to be used to build the questions. Must follow the assessment schema
 * @param {Array} initProps.questionData.options - options for the question
 * @param {String} initProps.questionData.options[].key - key value for the option
 * @param {String} initProps.questionData.options[].label - label for the option
 * @param {String} initProps.questionData.type - type for the option (radio or checkbox)
 * @param {String} initProps.questionData.label - label for the question
 * @param {Number} initProps.questionIndex - index of the question
 * @param {String} initProps.value - value of the input
 * @param {Array} initProps.values - values of the input (for multiple choice)
 * @param {Function} initProps.handleInputChange - function to handle input change
 * @param {String} initProps.handleInputChange.value - value of the input
 * @param {Array} initProps.handleInputChange.values - values of the input (for multiple choice)
 * @param {String} initProps.handleInputChange.name - name of the input
 * @returns {JSX.Element} - Assessment Form Screen Field Group component
 */
export default function AssessmentFormCheckGroup({
  questionData,
  questionIndex,
  handleInputChange = () => ({ value: '', values: [], name: '' })
}) {
  // State
  const [questionValue, setQuestionValue] = useState('')
  const [questionValues, setQuestionValues] = useState([])

  // Get the question name, type, and options
  const questionName = questionData.name || ''
  const questionType = questionData.type || ''
  const questionTypeGroup = questionType.split('-')[0]
  const questionOptions = questionData.options || []

  /**
   * Click handler for the input. Passes the value of the input and the values of the input (for multiple choice)
   * to the parent component via the handleInputChange function
   * @param {Object} event - event object
   */
  const onClick = event => {
    // If there is no event or no target, return
    if (!event?.target) return
    try {
      let values = questionValues
      const { value, name } = event.target

      // If the question is a radio button, set the value to the current value
      if (questionTypeGroup === 'radio') {
        setQuestionValue(value)
        handleInputChange({ value, name })
      }

      // If the question is a checkbox, add the value to the current values
      else if (questionTypeGroup === 'checkbox') {
        // Get the checked state of the checkbox
        const isChecked = event.target.checked

        // If the checkbox is checked, add the value to the current values
        // If the checkbox is unchecked, remove the value from the current values
        values = isChecked
          ? [...values, value]
          : values.filter(val => val !== value)
        setQuestionValues(values)
        handleInputChange({ values, name })
      }
    } catch (error) {
      console.error(error)
    }
  }

  // If we don't have screen data, return null
  if (!questionData) return null

  return (
    <div
      className={cn('assessment-form-check-group', questionTypeGroup)}
      key={questionIndex}
    >
      {questionData.label && (
        <p className='assessment-form-check-group-label muted'>
          {questionData.label}
        </p>
      )}
      {/* Is this a multiple-choice? */}
      {questionOptions.map((option, optionIndex) => (
        <Form.Check
          className={cn('assessment-form-question-option', {
            [questionTypeGroup]: true
          })}
          label={option.label}
          key={optionIndex}
          id={`${questionName}-${optionIndex}`}
          name={questionName}
          value={option.key}
          onClick={onClick}
          type={questionTypeGroup}
          defaultChecked={
            questionTypeGroup === 'checkbox'
              ? questionValues.includes(option.key)
              : questionValue === option.key
          }
        />
      ))}
    </div>
  )
}
